import { useCallback, useMemo, useState } from 'react';
import type { ListingColumn } from '@/shared/ui/components/basic/listing-table/types';
import { compareByColumn, type SortDir } from '@/shared/ui/components/basic/listing-table/sort-comparator';

export interface UseTriStateSortResult<T> {
  /** The column key currently sorted on, or `null` in default order. */
  sortKey: string | null;
  /** `'asc' | 'desc' | null` — `null` means default (unsorted) order. */
  sortDir: SortDir;
  /**
   * Advances the tri-state cycle for a column: a fresh column starts `asc`,
   * the active column goes `asc -> desc -> default`. Selecting a different
   * column restarts at `asc`. Non-sortable columns are ignored.
   */
  toggleSort: (col: ListingColumn<T>) => void;
  /**
   * Sorts a full dataset with the shared type-aware, nulls-last, pt-BR
   * comparator. Returns the input untouched while in default order, so a
   * caller can sort the WHOLE set before paginating — the fix for the
   * page-scoped sort bug.
   */
  sortData: (data: T[]) => T[];
}

/**
 * Owns the `{ sortKey, sortDir }` tri-state cycle (`asc -> desc -> default`)
 * for a table whose rows a caller holds directly, so the full dataset is
 * sorted ABOVE pagination rather than one already-sliced page.
 *
 * `ListingTable` keeps its own internal state for the common case; this hook
 * is for pages that paginate client-side (Leases) or thread the sort into a
 * server request, where the sort decision must live outside the table.
 */
export function useTriStateSort<T>(columns: ListingColumn<T>[]): UseTriStateSortResult<T> {
  const [state, setState] = useState<{ sortKey: string | null; sortDir: SortDir }>({
    sortKey: null,
    sortDir: null,
  });
  const { sortKey, sortDir } = state;

  const toggleSort = useCallback((col: ListingColumn<T>) => {
    if (!col.sortable) return;
    setState((prev) => {
      const isNewCol = prev.sortKey !== col.key;
      const nextDir: SortDir = isNewCol ? 'asc' : prev.sortDir === 'asc' ? 'desc' : null;
      return { sortKey: nextDir ? col.key : null, sortDir: nextDir };
    });
  }, []);

  const sortData = useCallback(
    (data: T[]): T[] => {
      if (!sortKey || !sortDir) return data;
      const col = columns.find((c) => c.key === sortKey);
      if (!col) return data;
      return [...data].sort((a, b) => compareByColumn(col, a, b, sortDir));
    },
    [columns, sortKey, sortDir],
  );

  return useMemo(
    () => ({ sortKey, sortDir, toggleSort, sortData }),
    [sortKey, sortDir, toggleSort, sortData],
  );
}
